import React from "react";
import { PROVIDER_TIER_OPTIONS, ProviderTier } from "../../types/provider";

interface ProviderTierSelectorProps {
  value: ProviderTier | null;
  onChange: (value: ProviderTier | null) => void;
  disabled?: boolean;
}

/**
 * Segmented control for the provider access tier (PWA-2335). Writes a machine
 * token (`full_access` | `academy`) into the provider `data.tier` field, or
 * null when cleared. Tier is optional, so an unselected state is valid.
 */
const ProviderTierSelector: React.FC<ProviderTierSelectorProps> = ({ value, onChange, disabled }) => {
  return (
    <div className="flex items-center gap-3">
      <div
        role="group"
        aria-label="Provider tier"
        className="inline-flex items-center rounded-lg border border-gray-200 dark:border-rp-separator bg-white dark:bg-rp-surface p-0.5"
      >
        {PROVIDER_TIER_OPTIONS.map(option => {
          const isActive = value === option.value;
          return (
            <button
              key={option.value}
              type="button"
              disabled={disabled}
              aria-pressed={isActive}
              onClick={() => onChange(option.value)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed ${
                isActive
                  ? "bg-rp-amber text-white"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>
      {value !== null && !disabled && (
        <button
          type="button"
          onClick={() => onChange(null)}
          className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
        >
          Clear
        </button>
      )}
    </div>
  );
};

export default ProviderTierSelector;
